import { z } from 'zod';

import { validateSQLQuery } from '../utils';

export enum ChartTypeEnum {
  TABLE = 'table',
  NUMBER = 'number',
  FUNNEL = 'funnel',
  BAR = 'bar',
  LINE = 'line',
  PIE = 'pie',
}

export const createChartRequest = z.object({
  body: z.object({
    projectId: z.string(),
    boardId: z.string(),
    query: z
      .string()
      .min(1, 'SQL query is required')
      .refine(
        (query) => {
          const validation = validateSQLQuery(query);
          return validation == null;
        },
        {
          message: 'Invalid SQL query',
        },
      ),
    chart_type: z.nativeEnum(ChartTypeEnum, {
      required_error: 'Please select a chart type',
    }),
    title: z.string().min(1, 'Chart title is required'),
    description: z.string().optional(),
    x_axis: z.string().optional(),
    y_axis: z.array(z.string()).optional(),
  }),
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
});

export type CreateChartRequest = z.infer<typeof createChartRequest>;

export type CreateChartResponse = String;

export const getChartsByProjectAndBoardRequest = z.object({
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
  query: z.object({
    boardId: z.string(),
  }),
});

export type GetChartsByProjectAndBoardRequest = z.infer<
  typeof getChartsByProjectAndBoardRequest
>;
export type GetChartsByProjectAndBoardResponse = {
  charts: ChartResponse[];
  board: PublicBoardSettingsType;
};

export const getChartRequest = z.object({
  params: z.object({
    chartId: z.string(),
    teamId: z.string().uuid(),
    projectId: z.string(),
    boardId: z.string(),
  }),
});

export type GetChartRequest = z.infer<typeof getChartRequest>;
export type GetChartResponse = ChartType;

export const ChartObjectType = z.object({
  chart_type: z.nativeEnum(ChartTypeEnum),
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  query: z.string(),
  project_id: z.string(),
  board_id: z.string(),
  created_at: z.date(),
  updated_at: z.date().nullable(),
  x_axis: z.string().nullable(),
  y_axis: z.array(z.string()).nullable(),
});

export type ChartType = z.infer<typeof ChartObjectType>;

export const ChartResponseType = z.object({
  chart_type: z.nativeEnum(ChartTypeEnum),
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  results: z.string(),
  x_axis: z.string().optional(),
  y_axis: z.array(z.string()).optional(),
});

export type ChartResponse = z.infer<typeof ChartResponseType>;

export const deleteChartRequest = z.object({
  body: z.object({
    chartId: z.string(),
    projectId: z.string(),
    boardId: z.string(),
  }),
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
});

export type DeleteChartRequest = z.infer<typeof deleteChartRequest>;

export type DeleteChartResponse = String;

export const editChartRequest = z.object({
  body: z.object({
    chartId: z.string(),
    projectId: z.string(),
    boardId: z.string(),
    query: z
      .string()
      .min(1, 'SQL query is required')
      .refine(
        (query) => {
          const validation = validateSQLQuery(query);
          return validation == null;
        },
        {
          message: 'Invalid SQL query',
        },
      ),
    chart_type: z.nativeEnum(ChartTypeEnum, {
      required_error: 'Please select a chart type',
    }),
    title: z.string().min(1, 'Chart title is required'),
    description: z.string().optional(),
    x_axis: z.string().optional(),
    y_axis: z.array(z.string()).optional(),
  }),
  params: z.object({
    teamId: z.string().uuid(),
    projectId: z.string(),
  }),
});

export type EditChartRequest = z.infer<typeof editChartRequest>;

export type EditChartResponse = String;

export const PublicBoardSettings = z.object({
  id: z.string(),
  created_at: z.date(),
  updated_at: z.date().nullable(),
  project_id: z.string(),
  enabled: z.boolean(),
  title: z.string().optional(),
  description: z.string().nullable(),
});

export type PublicBoardSettingsType = z.infer<typeof PublicBoardSettings>;

export const updatePublicBoardSettingsRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    boardId: z.string(),
  }),
  body: z.object({
    id: z.string(),
    isPublic: z.boolean(),
  }),
});

export type UpdatePublicBoardSettingsRequest = z.infer<
  typeof updatePublicBoardSettingsRequest
>;
export type UpdatePublicDashboardSettingsResponse = PublicBoardSettingsType;

export const getPublicBoardRequest = z.object({
  params: z.object({
    id: z.string(),
  }),
});
export type GetPublicBoardRequest = z.infer<typeof getPublicBoardRequest>;
export type GetPublicBoardResponse = {
  charts: ChartResponse[];
  board: PublicBoardSettingsType;
};

export const getBoardRequest = z.object({
  params: z.object({
    projectId: z.string(),
    boardId: z.string(),
  }),
});

export type GetBoardRequest = z.infer<typeof getBoardRequest>;
export type GetBoardResponse = PublicBoardSettingsType;

export const createBoardRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
  }),
  body: z.object({}),
});

export type CreateBoardRequest = z.infer<typeof createBoardRequest>;
export type CreateBoardResponse = PublicBoardSettingsType;

export const getBoardsRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
  }),
});

export type GetBoardsRequest = z.infer<typeof getBoardsRequest>;
export type GetBoardsResponse = PublicBoardSettingsType[];

export const deleteBoardRequest = z.object({
  params: z.object({
    boardId: z.string(),
    projectId: z.string(),
  }),
});

export type DeleteBoardRequest = z.infer<typeof deleteBoardRequest>;
export type DeleteBoardResponse = PublicBoardSettingsType;

export const updateBoardRequest = z.object({
  params: z.object({
    teamId: z.string(),
    projectId: z.string(),
    boardId: z.string(),
  }),
  body: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
  }),
});

export type UpdateBoardRequest = z.infer<typeof updateBoardRequest>;
export type UpdateBoardResponse = PublicBoardSettingsType;
